import type { MissionKindEnum } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';

import { MissionSpaceEnum } from '../enums/mission-space.enum';

import { missionKindProfileOf } from './mission-kind-profile.util';

export const MISSION_SPACE_PARAM = 'missionSpace';
export const COLLABORATION_ID_PARAM = 'collaborationId';

const SUB_MISSIONS_SEGMENT = 'sous-missions';

// The space of a mission is the URL prefix between its folder and its id:
// "missions", "collaborations", or its collaboration sub-missions for a
// sub-mission nested in its collaboration.
export type MissionSpacePath = string;

export const subMissionSpaceOf = (collaborationId: string): MissionSpacePath =>
  `${MissionSpaceEnum.Collaborations}/${collaborationId}/${SUB_MISSIONS_SEGMENT}`;

export const isSubMissionSpace = (space: MissionSpacePath): boolean =>
  space.startsWith(`${MissionSpaceEnum.Collaborations}/`);

export const isMissionRoutePath = (path: string): boolean =>
  path.includes(`:${MISSION_SPACE_PARAM}/`);

// The route of a mission page nested in the collaboration of its
// sub-mission: the space param spans several segments there, which a route
// param cannot.
export const toSubMissionRoutePath = (path: string): string =>
  path.replace(
    `:${MISSION_SPACE_PARAM}/`,
    `${MissionSpaceEnum.Collaborations}/:${COLLABORATION_ID_PARAM}/${SUB_MISSIONS_SEGMENT}/`,
  );

export const missionSpaceOf = (kind: MissionKindEnum): MissionSpaceEnum =>
  missionKindProfileOf(kind).space;

export const isMissionSpace = (
  segment: string | undefined,
): segment is MissionSpaceEnum =>
  Object.values<string>(MissionSpaceEnum).includes(segment ?? '');

type MissionOfSpace = Readonly<{
  kind: MissionKindEnum;
  // The collaboration of a sub-mission, nesting it; a sub-mission without it
  // (generic link) is served under "missions" and redirected once loaded.
  compositeMissionId?: string | null;
}>;

export const missionSpacePathOf = ({
  kind,
  compositeMissionId,
}: MissionOfSpace): MissionSpacePath =>
  missionKindProfileOf(kind).isWithinACollaboration && compositeMissionId
    ? subMissionSpaceOf(compositeMissionId)
    : missionSpaceOf(kind);

// Paths built outside of a mission page must name the space of the mission
// they point to.
export const buildMissionPath = (
  path: string,
  {
    folderId,
    missionId,
    ...mission
  }: Readonly<{ folderId: string; missionId: string }> & MissionOfSpace,
): string =>
  buildPath(path, {
    folderId,
    missionId,
    [MISSION_SPACE_PARAM]: missionSpacePathOf(mission),
  });

// The same page in the space of the mission, when the current URL uses
// another one (old link, generic link to a mission, sub-mission moved to
// another collaboration or detached from it).
export const findMissionSpaceRedirection = ({
  pathname,
  currentSpace,
  ...mission
}: Readonly<{
  pathname: string;
  currentSpace: MissionSpacePath;
}> &
  MissionOfSpace): string | null => {
  const expectedSpace = missionSpacePathOf(mission);

  return currentSpace === expectedSpace
    ? null
    : pathname.replace(`/${currentSpace}/`, `/${expectedSpace}/`);
};
